import React from 'react';
import { motion } from 'framer-motion';
import { produce } from 'immer';

/** The `transition` prop on a `Motion` */
interface TransitionProps {
  delay: number;
  duration: number;
  ease: 'easeIn' | 'easeOut';
}

/**
 * Animation for one property of a component
 */
export interface Animation {
  /** e.g. "opacity" or "color" */
  key: string;
  /** e.g. { opacity: 0 } */
  initial: string | number;
  /** e.g. { opacity: 1 } */
  animate: string | number;
  transition: TransitionProps;
}

/** All animations applied to a motion - generated from a set of `Animation`s */
interface MotionAnimationProps {
  initial: Record<string, string | number>;
  animate: Record<string, string | number>;
  transition: Record<string, TransitionProps>;
}

/** Config for rendering a single item during a multi-step animation   */
export interface Step {
  content: React.ComponentType;
  element?: 'div' | 'picture';
  animations: Animation[];
  className?: string;
}

export const generateAnimationProps = (
  animations: Animation[]
): MotionAnimationProps => {
  const props: MotionAnimationProps = animations.reduce(
    (acc, animation) => {
      return produce(acc, (draft) => {
        draft.initial[animation.key] = animation.initial;
        draft.animate[animation.key] = animation.animate;
        draft.transition[animation.key] = animation.transition;
      });
    },
    {
      initial: {},
      animate: {},
      transition: {},
    } as MotionAnimationProps
  );
  return props;
};

export const transformDelays = (steps: Step[]): Step[] => {
  const updatedSteps: Step[] = [];
  let totalDelay = 0;
  for (let stepIdx = 0; stepIdx < steps.length; stepIdx++) {
    const step = steps[stepIdx];

    let updatedAnimations: Animation[] = [];

    for (let animIdx = 0; animIdx < step.animations.length; animIdx++) {
      const animation = step.animations[animIdx];
      const { delay, duration } = animation.transition;

      const computedDelay = totalDelay + delay;

      updatedAnimations.push(
        produce(animation, (draft) => {
          draft.transition.delay = computedDelay;
        })
      );

      totalDelay = computedDelay + duration;
    }

    updatedSteps.push({ ...step, animations: updatedAnimations });
  }

  return updatedSteps;
};

export const StepBasedAnimation = ({
  steps,
  onComplete,
}: {
  steps: Step[];
  onComplete?: {
    handler: () => void;
    delay?: number;
  };
}) => {
  const onCompleteHandler = () => {
    setTimeout(
      () => {
        onComplete?.handler();
      },
      onComplete?.delay ?? 0
    );
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {transformDelays(steps).map((step, index) => {
        const { element, content: Content, animations, className } = step;
        const Element = motion[element || 'div'];
        return (
          <Element
            key={index}
            className={className}
            {...generateAnimationProps(animations)}
            onAnimationComplete={
              index === steps.length - 1 ? onCompleteHandler : undefined
            }
          >
            <Content />
          </Element>
        );
      })}
    </div>
  );
};
